import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api'

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: false,
})

// Garantir Content-Type correcto: JSON por defeito, mas nunca sobrepor FormData
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!(config.data instanceof FormData)) {
    config.headers = config.headers ?? {}
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json'
    }
  }
  return config
})

// Cache em memória — evita ler localStorage em cada pedido HTTP.
// É invalidado quando o Zustand store escreve novos tokens.
let _tokenCache: string | null = null
let _tenantCache: string | null = null

export function invalidateApiCache() {
  _tokenCache = null
  _tenantCache = null
}

// Parser de cookie reutilizável (executa apenas quando há domínio próprio)
function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const prefix = name + '='
  const cookies = document.cookie
  let start = 0
  while (start < cookies.length) {
    const eq = cookies.indexOf(prefix, start)
    if (eq === -1) break
    const end = cookies.indexOf(';', eq)
    return cookies.slice(eq + prefix.length, end === -1 ? undefined : end).trim()
  }
  return undefined
}

// Interceptor de pedidos: adiciona token JWT e contexto de tenant
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (typeof window !== 'undefined') {
    // Lê do cache em memória; popula a partir do localStorage apenas se necessário
    if (_tokenCache === null) {
      _tokenCache = localStorage.getItem('strada_access_token') ?? ''
    }
    if (_tokenCache && config.headers) {
      config.headers.Authorization = `Bearer ${_tokenCache}`
    }

    // Endpoints de autenticação nunca recebem tenant (saas_admin não pertence a nenhuma escola)
    const isAuthEndpoint = config.url?.includes('/auth/')
    if (!isAuthEndpoint && config.headers) {
      if (_tenantCache === null) {
        _tenantCache = localStorage.getItem('strada_tenant_slug') ?? ''
      }
      if (_tenantCache) {
        config.headers['X-Tenant-Slug'] = _tenantCache
      } else {
        // Tenant por domínio próprio — lido do cookie (definido pelo middleware Next.js)
        const customDomain = getCookie('strada_custom_domain')
        if (customDomain) config.headers['X-Forwarded-Host'] = customDomain
      }
    }
  }
  return config
})

// Mutex/fila para evitar múltiplos refresh concorrentes
let _isRefreshing = false
let _failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = []

const processQueue = (error: any, token: string | null = null) => {
  _failedQueue.forEach(prom => {
    if (error) prom.reject(error)
    else prom.resolve(token!)
  })
  _failedQueue = []
}

// Interceptor de respostas: renova token automaticamente
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // Não tentar refresh em endpoints de autenticação (evita loop em credenciais inválidas)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/otp') ||
      originalRequest.url?.includes('/auth/refresh')

    // Password temporária — forçar alteração antes de qualquer outra acção
    if (error.response?.status === 403 && (error.response.data as any)?.code === 'MUST_CHANGE_PASSWORD') {
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/alterar-password')) {
        window.location.href = '/alterar-password'
      }
      return Promise.reject(error)
    }

    // Acesso bloqueado por controlo de acesso (IP ou horário) — emite evento global
    if (error.response?.status === 403 && (error.response.data as any)?.accessControl) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('acesso-bloqueado', { detail: error.response.data }))
      }
      return Promise.reject(error)
    }

    // Licença expirada (402) — redirecionar escola_admin para página de renovação
    if (error.response?.status === 402) {
      if (typeof window !== 'undefined') {
        const role = document.cookie.match(/strada_role=([^;]+)/)?.[1]
        if (role === 'escola_admin' && !window.location.pathname.includes('/admin/licenca-expirada')) {
          window.location.href = '/admin/licenca-expirada'
        }
      }
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      // Se já há um refresh em andamento, enfileirar este pedido
      if (_isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          _failedQueue.push({ resolve, reject })
        }).then(token => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`
          }
          return api(originalRequest)
        }).catch(err => Promise.reject(err))
      }

      _isRefreshing = true

      try {
        const refreshToken = localStorage.getItem('strada_refresh_token')
        if (!refreshToken) throw new Error('Sem refresh token')

        const { data } = await axios.post(`${API_URL}/auth/refresh`, { refreshToken })

        localStorage.setItem('strada_access_token', data.accessToken)
        localStorage.setItem('strada_refresh_token', data.refreshToken)

        // Invalida cache para forçar leitura dos novos tokens
        _tokenCache = data.accessToken
        _tenantCache = null

        processQueue(null, data.accessToken)

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`
        }

        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        _tokenCache = null
        _tenantCache = null
        localStorage.removeItem('strada_access_token')
        localStorage.removeItem('strada_refresh_token')
        localStorage.removeItem('strada_user')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        _isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
